/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateSignedDocumentRequest } from '../models/create-signed-document-request';
import { FileUrl } from '../models/file-url';
import { SignedDocument } from '../models/signed-document';


/**
 * Signed Documents API
 */
@Injectable({
  providedIn: 'root',
})
export class SignedDocumentsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation createSignedDocument
   */
  static readonly CreateSignedDocumentPath = '/signed-documents';

  /**
   * Create signature request for the document.
   *
   * Create signature request for the document. The request is checked before Visma Sign is contacted: it answers 400 when the document already has a signature request, when it would invite nobody (`signers` is empty and `sign_as_inviter` is not true), when a signer has neither an email address nor an SMS number, has an invalid email address, or has an SMS number that is not a valid phone number (the message names the signer by its index, e.g. `signers[1]`), or when `name` is empty. When `sign_as_inviter` is true and Corona Capital's signer is not configured on the server, or is configured with an invalid email address or phone number, it answers 500 with a message saying so. A failure from Visma Sign or in reaching it, such as rejected credentials or a refused connection, answers 500 "Internal server error", as does any other failure on the server, such as one to read the document.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createSignedDocument()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSignedDocument$Response(params: {
    context?: HttpContext
    body: CreateSignedDocumentRequest
  }
): Observable<StrictHttpResponse<SignedDocument>> {

    const rb = new RequestBuilder(this.rootUrl, SignedDocumentsService.CreateSignedDocumentPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SignedDocument>;
      })
    );
  }

  /**
   * Create signature request for the document.
   *
   * Create signature request for the document. The request is checked before Visma Sign is contacted: it answers 400 when the document already has a signature request, when it would invite nobody (`signers` is empty and `sign_as_inviter` is not true), when a signer has neither an email address nor an SMS number, has an invalid email address, or has an SMS number that is not a valid phone number (the message names the signer by its index, e.g. `signers[1]`), or when `name` is empty. When `sign_as_inviter` is true and Corona Capital's signer is not configured on the server, or is configured with an invalid email address or phone number, it answers 500 with a message saying so. A failure from Visma Sign or in reaching it, such as rejected credentials or a refused connection, answers 500 "Internal server error", as does any other failure on the server, such as one to read the document.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createSignedDocument$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSignedDocument(params: {
    context?: HttpContext
    body: CreateSignedDocumentRequest
  }
): Observable<SignedDocument> {

    return this.createSignedDocument$Response(params).pipe(
      map((r: StrictHttpResponse<SignedDocument>) => r.body as SignedDocument)
    );
  }

  /**
   * Path part for operation getSignedDocumentUrl
   */
  static readonly GetSignedDocumentUrlPath = '/signed-documents/{document_id}/file';

  /**
   * Get a link to the contents of the signed document.
   *
   * Get a link to the contents of the signed document
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSignedDocumentUrl()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSignedDocumentUrl$Response(params: {
    document_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<FileUrl>> {

    const rb = new RequestBuilder(this.rootUrl, SignedDocumentsService.GetSignedDocumentUrlPath, 'get');
    if (params) {
      rb.path('document_id', params.document_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FileUrl>;
      })
    );
  }

  /**
   * Get a link to the contents of the signed document.
   *
   * Get a link to the contents of the signed document
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getSignedDocumentUrl$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSignedDocumentUrl(params: {
    document_id: number;
    context?: HttpContext
  }
): Observable<FileUrl> {

    return this.getSignedDocumentUrl$Response(params).pipe(
      map((r: StrictHttpResponse<FileUrl>) => r.body as FileUrl)
    );
  }

  /**
   * Path part for operation remindInvitees
   */
  static readonly RemindInviteesPath = '/signed-documents/{document_id}/reminders';

  /**
   * Send a reminder to the invitees about signing the dcoument.
   *
   * Send a reminder to the invitees about signing the dcoument
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `remindInvitees()` instead.
   *
   * This method doesn't expect any request body.
   */
  remindInvitees$Response(params: {
    document_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, SignedDocumentsService.RemindInviteesPath, 'post');
    if (params) {
      rb.path('document_id', params.document_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Send a reminder to the invitees about signing the dcoument.
   *
   * Send a reminder to the invitees about signing the dcoument
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `remindInvitees$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  remindInvitees(params: {
    document_id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.remindInvitees$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation cancelSigning
   */
  static readonly CancelSigningPath = '/signed-documents/{document_id}/cancellations';

  /**
   * Cancel the document signing process.
   *
   * Cancel the document signing process
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `cancelSigning()` instead.
   *
   * This method doesn't expect any request body.
   */
  cancelSigning$Response(params: {
    document_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, SignedDocumentsService.CancelSigningPath, 'post');
    if (params) {
      rb.path('document_id', params.document_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Cancel the document signing process.
   *
   * Cancel the document signing process
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `cancelSigning$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  cancelSigning(params: {
    document_id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.cancelSigning$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
